import type { ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@shared/ui/tooltip";
import { Spinner } from "@shared/ui/spinner";
import { Boundary } from "@shared/ui/Boundary";
import { queryClient } from "@shared/api";
import { Auth } from "@pages/auth";
import { Shell } from "@pages/workspace";
import { useSession, useSessionBoot } from "@entities/session";
import { StoreProvider } from "@features/chat";
import { UpdateBoundary } from "@features/updater";
import { useAppTitlebar } from "@widgets/titlebar";
import { AppSurface } from "./AppSurface";
import { Backdrop } from "./Backdrop";

function Gate() {
  const me = useSession((state) => state.me);
  const loading = useSession((state) => state.loading);
  useAppTitlebar(
    {
      subtitle: loading ? "загрузка" : "вход",
      browserVisible: false,
    },
    !me,
  );

  if (loading) {
    return (
      <div className="auth">
        <Spinner />
      </div>
    );
  }

  if (!me) return <Auth />;

  return (
    <StoreProvider>
      <Shell />
    </StoreProvider>
  );
}

export function App() {
  /*
   * Провайдер стоит выше входа, а не внутри `Gate`: запрос есть и на экране
   * входа он не нужен, зато при выходе `Gate` перерисовывается — и провайдер
   * под ним унёс бы кэш вместе с собой в тот же миг, когда его чистят руками.
   * Один клиент на всё время работы вкладки, уборка — в `StoreProvider`.
   */
  return (
    <UpdateBoundary renderCoreSurface={(content) => <CoreSurface>{content}</CoreSurface>}>
      <CoreSurface>
        <SessionApp />
      </CoreSurface>
    </UpdateBoundary>
  );
}

/**
 * Один и тот же offline-safe каркас проходит health probe и затем держит
 * обычное приложение. Сессия, websocket, media и пользовательские данные
 * остаются только в `SessionApp` и до ACK не монтируются.
 */
function CoreSurface({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {/*
        Провайдер подсказок один на приложение: он держит общую задержку и
        правило «вторая подряд показывается сразу». По одному на месте вызова
        это правило пропадает — каждая подсказка ждала бы свою паузу.
      */}
      <TooltipProvider>
        {/*
          Последняя граница — на случай, когда сорвалось то, что выше всех
          участков: сама оболочка, провайдеры, экран входа. Внутренние границы
          стоят по участкам и ловят раньше; сюда доходит только то, после чего
          показывать всё равно нечего.
        */}
        <Boundary area="приложение">
          <Backdrop />
          <AppSurface>{children}</AppSurface>
        </Boundary>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

/** Сессия не просыпается, пока стартовая проверка обновления держит вход. */
function SessionApp() {
  useSessionBoot();
  return <Gate />;
}
